import type { TerrainLayer } from './data';

export class AudioManager {
  private context?: AudioContext;
  private master?: GainNode;
  volume = .7;

  resume(): void {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.context.destination);
    }
    void this.context.resume();
  }

  setVolume(value: number): void {
    this.volume = value;
    if (this.master && this.context) this.master.gain.setTargetAtTime(value, this.context.currentTime, .02);
  }

  impact(layer: TerrainLayer): void {
    // Each stratum gets its own body so digging reads by ear: soft soil thud, damped
    // clay, sharp stone crack, deep rock boom.
    const profile: Record<TerrainLayer, { f: number; dur: number; type: OscillatorType; g: number; nDur: number; nCut: number; nGain: number }> = {
      topsoil: { f: 88, dur: .10, type: 'triangle', g: .20, nDur: .13, nCut: 520, nGain: .14 },
      clay:    { f: 120, dur: .085, type: 'sine', g: .21, nDur: .09, nCut: 620, nGain: .10 },
      stone:   { f: 188, dur: .05, type: 'square', g: .16, nDur: .06, nCut: 2600, nGain: .15 },
      deep:    { f: 70, dur: .16, type: 'sine', g: .24, nDur: .14, nCut: 360, nGain: .09 },
      crystal: { f: 320, dur: .07, type: 'sine', g: .17, nDur: .05, nCut: 3400, nGain: .12 },
      core:    { f: 54, dur: .2, type: 'sine', g: .26, nDur: .17, nCut: 300, nGain: .1 },
    };
    const p = profile[layer];
    this.tone(p.f, p.dur, p.type, p.g);
    this.noise(p.nDur, p.nCut, p.nGain);
    if (layer === 'stone') this.tone(430, .045, 'square', .08);  // sharp crack overtone
    if (layer === 'deep') this.tone(46, .2, 'sine', .12);        // sub-bass boom
    if (layer === 'crystal') this.tone(660, .12, 'sine', .07);   // glassy chime overtone
    if (layer === 'core') this.tone(40, .26, 'sine', .13);       // molten rumble
  }

  collect(rare: boolean): void {
    this.tone(rare ? 880 : 620, .14, 'sine', .16);
    window.setTimeout(() => this.tone(rare ? 1320 : 820, .12, 'sine', .12), 55);
  }
  sell(): void { this.tone(420, .09, 'square', .13); window.setTimeout(() => this.tone(720, .13, 'sine', .15), 70); }
  deny(): void { this.tone(95, .16, 'sawtooth', .12); }
  upgrade(): void { [330, 495, 660].forEach((frequency, index) => window.setTimeout(() => this.tone(frequency, .12, 'triangle', .13), index * 70)); }

  private tone(frequency: number, duration: number, type: OscillatorType, gain: number): void {
    this.resume();
    if (!this.context || !this.master) return;
    const oscillator = this.context.createOscillator();
    const envelope = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    envelope.gain.setValueAtTime(gain, this.context.currentTime);
    envelope.gain.exponentialRampToValueAtTime(.001, this.context.currentTime + duration);
    oscillator.connect(envelope).connect(this.master);
    oscillator.start(); oscillator.stop(this.context.currentTime + duration);
  }

  private noise(duration: number, cutoff: number, gain: number): void {
    if (!this.context || !this.master) return;
    const length = Math.floor(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const source = this.context.createBufferSource(); source.buffer = buffer;
    const filter = this.context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = cutoff;
    const envelope = this.context.createGain(); envelope.gain.value = gain;
    source.connect(filter).connect(envelope).connect(this.master); source.start();
  }
}
